"use client";
import { useEffect, useState, useContext, useRef, useCallback } from "react";
import Link from "next/link";
import Image from "next/image";
import { TrashIcon } from "~/_components/icons";
import { StoreContext } from "~/app/store-provider";
import { formatInTimeZone } from "date-fns-tz";
import { CloseIcon } from "~/_components/icons";
import { getUrlSlug, formatTag } from "~/_services/helpers";
import {
  BarIcon,
  EventIcon,
  ActivityIcon,
  RestaurantIcon,
} from "~/_components/icons";
import { StoreI, ListingType } from "~/interfaces";

export default function LikeMenu() {
  const likeMenuRef = useRef<HTMLDivElement>(null);
  const likeMenuTitleRef = useRef<HTMLDivElement>(null);
  const likeMenuRemoveBtnRef = useRef<HTMLDivElement>(null);
  const [store, setStore] = useContext(StoreContext);
  const [likedListings, setLikedListings] = useState<any>([]);
  const [menuHeight, setMenuHeight] = useState(10);
  const getIcon = (listingType: string) => {
    return ListingType.ACTIVITY === listingType ? (
      <ActivityIcon className="h-[.80rem] pr-2 md:h-[.90rem] lg:h-4" />
    ) : ListingType.BAR === listingType ? (
      <BarIcon className="h-[.80rem] pr-2 md:h-[.90rem] lg:h-4" />
    ) : ListingType.EVENT === listingType ? (
      <EventIcon className="h-[.80rem] pr-2 md:h-[.90rem] lg:h-4" />
    ) : ListingType.RESTAURANT === listingType ? (
      <RestaurantIcon className="h-[.80rem] pr-2 md:h-[.90rem] lg:h-4" />
    ) : (
      ""
    );
  };
  const removeLiked = (listingId?: string) => {
    setTimeout(() => {
      if (listingId) {
        localStorage.removeItem("fun-like-" + listingId);
      } else {
        Object.entries(localStorage).map((item) => {
          if (item[0].includes("fun-like")) {
            localStorage.removeItem(item[0]);
          }
        });
      }
      window.dispatchEvent(new Event("storage"));
    }, 600);
  };

  // get ALL liked listings from local storage
  const getStorage = useCallback(() => {
    const storageLikedListings = Object.entries(localStorage)
      .filter((item) => item[0].includes("fun-like"))
      .map((item) => item[1]);
    setStore((prevStore: StoreI) => ({
      ...prevStore,
      app: {
        ...prevStore.app,
        likedListings: storageLikedListings.length,
      },
    }));

    setLikedListings(storageLikedListings);
  }, [setLikedListings, setStore]);

  useEffect(() => {
    // init state
    getStorage();
    // listen for state changes in local storage
    window.addEventListener("storage", getStorage);
    return () => {
      window.removeEventListener("storage", getStorage);
    };
  }, [getStorage, store.app.city]);

  useEffect(() => {
    // guard
    if (
      !likeMenuRef.current &&
      !likeMenuTitleRef.current &&
      !likeMenuRemoveBtnRef.current
    )
      return;

    const menuTop = likeMenuRef.current?.getBoundingClientRect().top ?? 0;
    const titleHeight =
      likeMenuTitleRef.current?.getBoundingClientRect().height ?? 0;
    const removeBtnHeight =
      likeMenuRemoveBtnRef.current?.getBoundingClientRect().height ?? 0;

    setMenuHeight(menuTop + titleHeight + removeBtnHeight);
  }, [store.app.menu.isLikeActive]);

  return (
    <div
      ref={likeMenuRef}
      className={`${store.app.menu.isLikeActive ? `right-0` : `-right-[24rem] lg:-right-[32rem]`} fun-like-menu transition-right absolute top-14 z-10 flex w-full max-w-[24rem] flex-col overflow-y-hidden border-t border-brown bg-beige font-[600] uppercase text-brown duration-300 ease-linear lg:top-20 lg:max-w-[32rem]`}
    >
      <div
        ref={likeMenuTitleRef}
        className="fun-box-shadow-bottom relative z-10 flex h-12 justify-center p-4 lg:h-14"
      >
        <span className="text-[1.2rem] font-[500] tracking-[1] lg:text-[1.6rem]">
          Favorites
        </span>
        <span
          className="absolute right-0 top-0 px-[.9rem] py-[.7rem] lg:px-[1.35rem] lg:py-[.7rem]"
          aria-label="close like menu"
          onClick={() => {
            setStore((prevStore: StoreI) => ({
              ...prevStore,
              app: {
                ...prevStore.app,
                menu: {
                  ...prevStore.app.menu,
                  isLikeActive: false,
                },
              },
            }));
          }}
        >
          <CloseIcon className="h-7 cursor-pointer text-brown lg:h-9" />
        </span>
      </div>

      {likedListings.length === 0 ? (
        <div className="flex flex-col justify-center px-4 py-8 text-center leading-7">
          You have no favorite{" "}
          <span className="whitespace-nowrap">listings saved.</span>
          Click hearts to save them.
        </div>
      ) : (
        <>
          <ul
            style={{ height: `calc(100vh - ${menuHeight}px)` }}
            className={`fun-scrollbar overflow-x-hidden overflow-y-scroll px-0 py-4`}
          >
            {likedListings.map((listing: string) => {
              const listingArr = listing.split("]|[");
              const listingId = listingArr[0],
                listingType = listingArr[1],
                listingSubtype = listingArr[2],
                listingTitle = listingArr[3],
                listingDate = listingArr[4] !== "null" ? listingArr[4] : null,
                listingImgSrc = listingArr[5];

              return (
                listingId &&
                listingType &&
                listingTitle &&
                listingImgSrc !== "undefined" && (
                  <li
                    key={`like-${listingId}`}
                    fun-id="fun-like-item"
                    className="flex w-full items-stretch p-3 leading-5"
                  >
                    <div className="">
                      <Link
                        className="relative inline-block h-[6rem] w-[8rem] cursor-pointer lg:h-[8rem] lg:w-[12rem]"
                        href={`/listings/${listingId}?title=${getUrlSlug(listingTitle)}`}
                        scroll={false}
                        aria-label={listingTitle}
                        prefetch={true}
                        onClick={() => {
                          setStore((prevStore: StoreI) => ({
                            ...prevStore,
                            app: {
                              ...prevStore.app,
                              menu: {
                                ...prevStore.app.menu,
                                isLikeActive: false,
                              },
                            },
                          }));
                        }}
                      >
                        {listingImgSrc && (
                          <Image
                            className="h-full w-full object-cover object-center"
                            quality={100}
                            fill
                            sizes="(max-width: 768px) 100vw, 80vw"
                            alt={listingTitle}
                            src={listingImgSrc || ""}
                          />
                        )}
                      </Link>
                    </div>
                    <div className="flex w-full">
                      <div className="flex-start flex w-full flex-col pl-3 pt-0 text-[1.25rem]">
                        {listingType && listingSubtype && (
                          <span
                            className={`fun-${listingType}-color mb-2 flex w-fit flex-nowrap items-center justify-center p-[.3rem] px-[.4rem] text-[.8rem] font-[600] tracking-[1] text-white lg:p-[.4rem] lg:px-[.5rem] lg:text-[1rem]`}
                          >
                            {getIcon(listingType)}
                            <span className="ml-0 whitespace-nowrap border-l-2 pl-2 leading-[1]">
                              {formatTag(listingSubtype)}
                            </span>
                          </span>
                        )}
                        <div className="flex w-full justify-between">
                          <Link
                            className={`${listingDate ? "line-clamp-3" : "line-clamp-4"} mb-1 text-[.9rem] font-[500] leading-[1.2] hover:underline lg:mb-2 lg:text-[1.1rem]`}
                            href={`/listings/${listingId}?title=${getUrlSlug(listingTitle)}`}
                            scroll={false}
                            aria-label={listingTitle}
                            prefetch={true}
                            onClick={() => {
                              setStore((prevStore: StoreI) => ({
                                ...prevStore,
                                app: {
                                  ...prevStore.app,
                                  menu: {
                                    ...prevStore.app.menu,
                                    isLikeActive: false,
                                  },
                                },
                              }));
                            }}
                          >
                            {listingTitle}
                          </Link>
                          <span
                            className="cursor-pointer self-center p-2 font-[600] underline lg:p-4"
                            aria-label={`remove ${listingTitle} from liked listings`}
                            onClick={(e) => {
                              e.currentTarget
                                .closest('[fun-id="fun-like-item"]')
                                ?.classList.add(
                                  "duration-[400ms]",
                                  "ease-linear",
                                  "opacity-0",
                                  "transition-opacity",
                                );
                              removeLiked(listingId);
                            }}
                          >
                            <TrashIcon className="size-6" />
                          </span>
                        </div>
                        {listingDate && store.app.city?.timezone && (
                          <span className="whitespace-nowrap text-[.9rem] font-[500] lg:text-[1rem]">
                            {formatInTimeZone(
                              listingDate,
                              store.app.city?.timezone,
                              "M-dd-yyyy h:mm aa",
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                )
              );
            })}
          </ul>
          <span
            ref={likeMenuRemoveBtnRef}
            className="align-center flex w-full cursor-pointer justify-center bg-orange p-4 font-[600] text-beige lg:p-6"
            aria-label="remove all liked listings"
            onClick={(e) => {
              e.currentTarget
                .closest("ul")
                ?.querySelectorAll('[fun-id="fun-like-item"]')
                .forEach((el) => {
                  el.classList.add(
                    "duration-[400ms]",
                    "ease-linear",
                    "opacity-0",
                    "transition-opacity",
                  );
                });

              removeLiked();
            }}
          >
            Remove ALL
          </span>
        </>
      )}
    </div>
  );
}
